import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { SEO } from '../App';
import { ChevronRight, Bot, CheckCircle2, ArrowRight } from 'lucide-react';
import { localizedRoutesMap } from '../routes';

export default function AiSolutions() {
  const { i18n } = useTranslation();
  const lang = i18n.language.split('-')[0];
  
  const content = {
    sq: {
      title: "Zgjidhje me Inteligjencë Artificiale (AI)",
      seoTitle: "Zgjidhje AI Kosovë | Automatizim - Vizioni Digjital",
      seoDesc: "Integrimi i zgjidhjeve AI dhe automatizimit për të rritur efikasitetin e biznesit tuaj.",
      heroSub: "Ne ofrojmë zgjidhje profesionale digjitale të përshtatura për nevojat e biznesit tuaj në Kosovë dhe rajon.",
      explanation: "Vizioni Digjital ndihmon bizneset përmes shërbimeve moderne, duke ju mundësuar të dominoni tregun.",
      includedTitle: "Çfarë përfshihet?",
      included: [
        "Planifikim dhe strategji e plotë",
        "Zbatim i standardeve më të fundit",
        "Optimizim për tregun e Kosovës",
        "Suport dhe mirëmbajtje"
      ],
      cta: "Fillo projektin tënd",
      ctaBtn: "Kërko Konsultim",
      related: "Eksploro më shumë shërbime",
      faqTitle: "Pyetjet më të shpeshta (FAQ)",
      faqs: [
        { q: "A ofroni shërbime në të gjithë Kosovën?", a: "Po, zyrat tona janë në Mitrovicë, por ne punojmë me biznese nga Prishtina, Prizreni, Peja dhe mbarë Kosova." }
      ]
    },
    en: {
      title: "Digital Solutions",
      seoTitle: "Digital Agency Services | Vizioni Digjital",
      seoDesc: "Professional digital services and solutions.",
      heroSub: "We offer professional digital solutions tailored to your business needs.",
      explanation: "Vizioni Digjital helps businesses through modern services.",
      includedTitle: "What's included?",
      included: [
        "Full planning and strategy",
        "Implementation of latest standards",
        "Market optimization",
        "Support and maintenance"
      ],
      cta: "Start your project",
      ctaBtn: "Request Consultation",
      related: "Explore more services",
      faqTitle: "Frequently Asked Questions (FAQ)",
      faqs: [
        { q: "Do you offer services internationally?", a: "Yes, we work with clients worldwide." }
      ]
    },
    de: {
      title: "Digitale Lösungen",
      seoTitle: "Digitale Dienstleistungen | Vizioni Digjital",
      seoDesc: "Professionelle digitale Dienste und Lösungen.",
      heroSub: "Wir bieten professionelle digitale Lösungen, die auf Ihre Geschäftsanforderungen zugeschnitten sind.",
      explanation: "Vizioni Digjital hilft Unternehmen durch moderne Dienstleistungen.",
      includedTitle: "Was ist inbegriffen?",
      included: [
        "Vollständige Planung und Strategie",
        "Umsetzung neuester Standards",
        "Marktoptimierung",
        "Support und Wartung"
      ],
      cta: "Starten Sie Ihr Projekt",
      ctaBtn: "Beratung anfordern",
      related: "Weitere Dienstleistungen erkunden",
      faqTitle: "Häufig gestellte Fragen (FAQ)",
      faqs: [
        { q: "Bieten Sie internationale Dienstleistungen an?", a: "Ja, wir arbeiten mit Kunden weltweit zusammen." }
      ]
    }
  };

  const currentContent = content[lang as keyof typeof content] || content.en;

  const serviceLabels = {
    sq: { WebDesign: 'Web Dizajn Kosovë', WebsiteDevelopment: 'Ndërtim Uebfaqesh', BusinessWebsites: 'Webfaqe për Biznese', Ecommerce: 'Krijim Webshop', SEOPage: 'SEO Kosovë', WebApplications: 'Zhvillim Softueri', LocalSEO: 'SEO Lokal Kosovë', AiSolutions: 'Zgjidhje AI', Branding: 'Branding Kosovë' },
    en: { WebDesign: 'Web Design Kosovo', WebsiteDevelopment: 'Website Development', BusinessWebsites: 'Business Websites', Ecommerce: 'E-commerce', SEOPage: 'SEO Kosovo', WebApplications: 'Software Development', LocalSEO: 'Local SEO Kosovo', AiSolutions: 'AI Solutions', Branding: 'Branding' },
    de: { WebDesign: 'Webdesign Kosovo', WebsiteDevelopment: 'Website-Entwicklung', BusinessWebsites: 'Business-Websites', Ecommerce: 'E-Commerce', SEOPage: 'SEO Kosovo', WebApplications: 'Softwareentwicklung', LocalSEO: 'Lokales SEO Kosovo', AiSolutions: 'KI-Lösungen', Branding: 'Branding' }
  };
  const lbls = serviceLabels[lang as keyof typeof serviceLabels] || serviceLabels.en;

  return (
    <>
      <SEO 
        title={currentContent.seoTitle}
        description={currentContent.seoDesc}
        includeSchema={true}
      />
      <div className="pt-32 pb-24 min-h-screen">
        <div className="max-w-4xl mx-auto px-6">
          
          <div className="flex items-center gap-2 text-sm text-white/50 mb-12">
            <Link to={localizedRoutesMap[lang]?.home || "/"} className="hover:text-primary-light transition-colors">Home</Link>
            <ChevronRight size={14} />
            <Link to={localizedRoutesMap[lang]?.services || "/services"} className="hover:text-primary-light transition-colors">Services</Link>
            <ChevronRight size={14} />
            <span className="text-white/80">{currentContent.title}</span>
          </div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-16">
            <div className="w-16 h-16 bg-primary/20 rounded-2xl flex items-center justify-center mb-8 border border-primary/30 text-primary-light">
              <Bot size={32} />
            </div>
            <h1 className="text-4xl md:text-6xl font-display font-bold mb-6 leading-tight">{currentContent.title}</h1>
            <p className="text-xl text-white/70 leading-relaxed max-w-2xl">{currentContent.heroSub}</p>
          </motion.div>

          <div className="space-y-12">
            <div className="glass-card p-8 md:p-12 rounded-3xl border border-white/5">
              <p className="text-lg text-white/80 leading-relaxed">{currentContent.explanation}</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="glass-card p-8 rounded-3xl border border-white/5">
                <h3 className="text-2xl font-bold mb-6">{currentContent.includedTitle}</h3>
                <ul className="space-y-4">
                  {currentContent.included.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="text-primary-light shrink-0 mt-1" size={20} />
                      <span className="text-white/80">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="glass-card p-8 md:p-12 rounded-3xl border border-white/5 mt-16">
              <h2 className="text-3xl font-bold mb-8">{currentContent.faqTitle}</h2>
              <div className="space-y-6">
                {currentContent.faqs.map((faq, i) => (
                  <div key={i} className="border-b border-white/10 pb-6 last:border-0 last:pb-0">
                    <h3 className="text-xl font-bold mb-3 text-primary-light">{faq.q}</h3>
                    <p className="text-white/70 leading-relaxed">{faq.a}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-r from-primary-dark/40 to-blue-900/40 p-8 md:p-12 rounded-3xl border border-primary/20 text-center mt-16">
              <h2 className="text-3xl font-bold mb-6">{currentContent.cta}</h2>
              <Link to={localizedRoutesMap[lang]?.contact || "/contact"} className="inline-flex items-center gap-2 bg-primary hover:bg-primary-light text-white px-8 py-4 rounded-xl font-bold transition-all shadow-lg hover:shadow-primary/25">
                {currentContent.ctaBtn} <ArrowRight size={20} />
              </Link>
            </div>
            
            <div className="pt-16 border-t border-white/10">
              <h3 className="text-2xl font-bold mb-8">{currentContent.related}</h3>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                 <Link to={localizedRoutesMap[lang]?.webDesign} className="p-6 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/5 transition-all text-center">
                    <h4 className="font-bold">{lbls.WebDesign}</h4>
                 </Link>
                 <Link to={localizedRoutesMap[lang]?.websiteDevelopment} className="p-6 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/5 transition-all text-center">
                    <h4 className="font-bold">{lbls.WebsiteDevelopment}</h4>
                 </Link>
                 <Link to={localizedRoutesMap[lang]?.seo} className="p-6 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/5 transition-all text-center">
                    <h4 className="font-bold">{lbls.SEOPage}</h4>
                 </Link>
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}
